import {login, logout} from '@ekultur/authentication';
import {Component, computed, effect, ElementRef, EventEmitter, inject, Input, Output, signal, ViewChild} from '@angular/core';
import {MatCard, MatCardContent} from '@angular/material/card';
import {MatButton, MatIconButton} from '@angular/material/button';
import {animate, style, transition, trigger} from '@angular/animations';
import {AnimatedPrimusLogoComponent} from '../splash-screen/animated-primus-logo/animated-primus-logo.component';
import {MatIcon} from '@angular/material/icon';
import {MatMenu, MatMenuTrigger} from '@angular/material/menu';
import {CdkTrapFocus} from '@angular/cdk/a11y';
import {MatTooltip} from '@angular/material/tooltip';
import {NgTemplateOutlet} from '@angular/common';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {TranslateModule} from '@ngx-translate/core';
import {PrimusBackendInstanceService} from '../core/primus-backend-instance.service';
import {environment} from '../../environments/environment';
import {FreshArticlePreviewComponent} from '../shared/fresh-article-preview/fresh-article-preview.component';
import {HibernationService} from '../shared/hibernation.service';
import {HibernationFlowService} from '../shared/hibernation-flow.service';

const animationTime = 200;
@Component({
    selector: 'app-hibernate-wakeup-overlay',
    imports: [
        MatCard,
        MatCardContent,
        MatButton,
        AnimatedPrimusLogoComponent,
        MatIconButton,
        MatIcon,
        MatMenuTrigger,
        MatMenu,
        CdkTrapFocus,
        MatTooltip,
        NgTemplateOutlet,
        MatProgressSpinner,
        TranslateModule,
        FreshArticlePreviewComponent
    ],
    templateUrl: './hibernate-wakeup-overlay.component.html',
    styleUrl: './hibernate-wakeup-overlay.component.scss',
    animations: [
        trigger('fadeInOut', [
            transition('void => *', [style({ opacity: '0' }), animate(animationTime)]),
            transition('* => void', [animate(animationTime, style({ opacity: '0' }))]),
            transition('* => *', [
                style({ opacity: '0' }),
                animate(animationTime, style({ opacity: '1' })),
            ]),
        ])
    ]
})
export class HibernateWakeupOverlayComponent {

  private readonly hibernationService = inject(HibernationService);
  private readonly hibernationFlow = inject(HibernationFlowService);

  // Never show wake overlay while museum selector is active.
  showOverlay = computed(() =>
    !this.hibernationFlow.backendSelectorVisible()
    && (this.hibernationService.isWakeFlowUiActive() || !!this.hibernationService.wakeupError())
  );
  // Human-friendly wake status (for example DB/Solr starting), produced by HibernationService.
  wakeupHint = this.hibernationService.wakeupHint;
  @Input() infoMessageKey: string | null = null;
  @Input() actionLabelKey: string | null = null;
  @Output() action = new EventEmitter<void>();

  userName = signal<string | null>(null);
  selectedInstance = signal<string | null>(null);

  @ViewChild('articleRef') articleRef: ElementRef;

  loadingTipsArticles = this.hibernationService.loadingUserTips;
  tipsArticles = this.hibernationService.userTips;

  currentTipIndex = signal(0);
  currentTip = computed(() => this.tipsArticles()?.[this.currentTipIndex()]);

  constructor() {
    effect(() => {
      try {
        if (this.showOverlay()) {

          this.selectedInstance.set(PrimusBackendInstanceService.getInstanceDetails()?.name ?? null);

          const idToken = localStorage.getItem('kit.id_token');
          if (idToken) {
            const userName = JSON.parse(b64DecodeUnicode(idToken.split('.')[1]))?.name;
            this.userName.set(userName ?? null);
          }
        }
      } catch (e) {
        console.error('failed to set username from id_token', e);
      }
    });

    effect(() => {
      if (this.tipsArticles()) {
        this.currentTipIndex.set(Math.floor(Math.random() * (this.tipsArticles()?.length || 0)));
      }
    });

  }

  onNext() {
    this.articleRef?.nativeElement?.scrollTo(0, 0);
    this.currentTipIndex.update(i => (i + 1) % this.tipsArticles()?.length || 0);
  }

  onPrevious() {
    this.articleRef?.nativeElement?.scrollTo(0, 0);
    this.currentTipIndex.update(i => (i - 1 + (this.tipsArticles()?.length || 0)) % (this.tipsArticles()?.length || 0));
  }

  onLogout() {
    const eCultureLogoutCallback = () => {
      PrimusBackendInstanceService.cleanInstanceDetails();

      setTimeout(() => {
        login({
          locationPostLogin: null,
          redirectUri: null,
          loginHost: environment.ekulturLoginGatewayURL
        })
      }, 500);
    }

    logout({
      signal: null,
      apiGateway: environment.ekulturAPIGatewayURL,
      onLogout: eCultureLogoutCallback
    });
  }

  onActionClick() {
    this.action.emit();
  }

}

// utf-8 safe decoding of base64
// source: https://stackoverflow.com/a/30106551
function b64DecodeUnicode(str: string) {
  return decodeURIComponent(Array.prototype.map.call(atob(str), function(c) {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
  }).join(''))
}
